/**
 * Interactions service — the CRM activity log (Airtable `Interactions` table).
 *
 * Every touch (call/email/meeting/action) on a Lead or an Opportunity is one Interaction row.
 * These rows drive the Lead's `Latest Interaction` rollup + `Rating`/`Days Since` formulas.
 *
 * React-on-Airtable: owns the table id + field names; returns normalized objects.
 *
 * Filtering note (Airtable rules): interactions-by-lead is resolved from the lead's own
 * `Interactions` link ids (reliable record ids) via a RECORD_ID() OR-filter — we do NOT filter on a
 * lookup of the lead's RECORD_ID() formula (those return empty via the API). Interactions-by-project
 * uses the `Project Record UID (from Projects)` text lookup with the FIND/ARRAYJOIN pattern, matching
 * services/projects.js.
 */

import { apiGet, apiGetAll, apiGetOne, apiPost, apiPatch, apiDelete } from './_api.js'

const TBL_INTER = 'tblrZNX03P3OjeSWw'
const TBL_LEAD  = 'tblMS59GDDKyq78us'

export const INTERACTION_TYPES = ['Phone Call', 'Video Conference', 'Email', 'Text Message', 'In Person', 'Action Taken']

export const LEAD_ACTIVITY_TYPES = [
  'Inbound Inquiry', 'Referral Received', 'Outbound Contact', 'Follow-Up Attempt',
  'Meeting/Call Scheduled', 'Engaged with Lead', 'Internal Update',
  'Lead Disengaged (Reason Provided)', 'Closed Out (No Response)', 'Converted to Prospect',
]

export const OPPORTUNITY_TYPES = [
  'Converted from Lead', 'Information Gathering', 'Proposal/Contract Posted',
  'Proposal/Contract Presented/Sent', 'Follow-Up', 'Client Questions/Clarifications',
  'Terms Discussed (Negotiation)', 'Decision (Won)', 'Decision (Lost)', 'Contract Executed',
  'Opportunity Closed - No Response', 'Opportunity Closed - No Go by Prospect',
  'Opportunity Closed - No Go by AJA', 'Opportunity Closed - Prospect Cancelled',
]

export const PLANNED_NEXT_TYPES = ['Phone Call', 'Video Conference', 'Email', 'Text Message', 'In Person']

/**
 * Maps a new lead's Lead Source (+ its sub-option, e.g. Inbound Inquiry Options) to the
 * Interaction {type, leadActivityType} that should exist the moment the lead is created — so
 * crmLeadNextStep.js's "no human touch yet" acknowledge rule never fires universally on a brand
 * new lead, even for sources that plainly already involved a real conversation. Passive sources
 * (nobody at the firm has responded yet) intentionally use the same 'Inbound Inquiry'/
 * 'Referral Received' values crmLeadNextStep.js's AUTO_TYPES already excludes from counting as a
 * human touch — this is additive data capture, not a change to that logic. Sources where the
 * firm already had a live encounter or made outbound contact (Networking, Event Attendance, Cold
 * Outreach) use 'Outbound Contact' deliberately, not 'Engaged with Lead' — the latter also
 * satisfies the qualification-conversation gate on its own and would overstate a brief event
 * encounter or a single cold email.
 */
export function interactionForLeadSource(source, sub) {
  switch (source) {
    case 'Inbound Inquiry':
      return { type: sub === 'Phone Call' ? 'Phone Call' : sub === 'Email' ? 'Email' : 'Action Taken', leadActivityType: 'Inbound Inquiry' }
    case 'Referral':
      return { type: 'Action Taken', leadActivityType: 'Referral Received' }
    case 'Networking':
    case 'Event Attendance':
      return { type: 'In Person', leadActivityType: 'Outbound Contact' }
    case 'Cold Outreach':
      return { type: 'Email', leadActivityType: 'Outbound Contact' }
    case 'Existing Database':
      return { type: 'Action Taken', leadActivityType: 'Internal Update' }
    case 'Marketing Campaign':
    case 'Web Search':
    case 'Social Media':
    case 'Other':
    default:
      return { type: 'Action Taken', leadActivityType: 'Inbound Inquiry' }
  }
}

function normInteraction(r) {
  const f = (r && r.fields) || {}
  const first = key => (Array.isArray(f[key]) ? f[key][0] : null) || null
  return {
    id: r.id,
    date: f['Date'] || null,
    type: f['Type'] || '',
    leadActivityType: f['Lead Activity Type'] || '',
    opportunityType: f['Opportunity Type'] || '',
    notes: f['Notes'] || '',
    teamMemberId: first('Team Member'),
    teamMember: f['Team Member Text'] || '',
    plannedNext: f['Planned Next Interaction'] || '',
    nextDate: f['Next Interaction Date'] || null,
    nextComplete: !!f['Next Interaction Complete'],
    voicemail: !!f['Voicemail'],
    leadId: first('Link to Lead'),
    projectIds: Array.isArray(f['Projects']) ? f['Projects'] : [],
    contactIds: Array.isArray(f['Contact']) ? f['Contact'] : [],
    raw: f,
  }
}

function sortByDateDesc(list) {
  return list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim)
   instead of throwing — surface it so a bad write shows up as a real error instead of
   silently doing nothing. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

/* ── Reads ───────────────────────────────────────────────────────────────── */

/** Fetch interactions by their record ids (chunked OR(RECORD_ID()=…) filter). */
export async function getInteractionsByIds(ids) {
  const list = Array.isArray(ids) ? ids.filter(Boolean) : []
  if (!list.length) return []
  const out = []
  for (let i = 0; i < list.length; i += 50) {
    const chunk = list.slice(i, i + 50)
    const formula = 'OR(' + chunk.map(id => `RECORD_ID()='${id}'`).join(',') + ')'
    const recs = await apiGet(TBL_INTER, 'filterByFormula=' + encodeURIComponent(formula))
    out.push(...recs.map(normInteraction))
  }
  return sortByDateDesc(out)
}

/** All interactions for a lead, newest first. Resolves the lead's linked Interaction ids first. */
export async function getInteractionsForLead(leadId) {
  const lead = await apiGetOne(TBL_LEAD, leadId)
  const ids = lead && Array.isArray(lead.fields?.['Interactions']) ? lead.fields['Interactions'] : []
  return getInteractionsByIds(ids)
}

/** All interactions for an opportunity (Project), newest first. */
export async function getInteractionsForOpportunity(projectId) {
  const formula = `FIND("${projectId}",ARRAYJOIN({Project Record UID (from Projects)},","))`
  const recs = await apiGet(TBL_INTER, 'filterByFormula=' + encodeURIComponent(formula))
  return sortByDateDesc(recs.map(normInteraction))
}

/**
 * All opportunity-linked interactions from the last N days, in ONE bulk query — the cheap
 * "list view" urgency tier (days-since-activity per opportunity) that avoids an N+1 per-row
 * fetch when the whole pipeline list loads. Older activity is treated as "nothing recent" by
 * callers, so the window doesn't need to be exhaustive. Intentionally client-computed, not an
 * Airtable rollup/formula field — see docs/MIGRATION_DERIVATIONS.md.
 */
export async function getRecentOpportunityInteractions({ days = 90 } = {}) {
  const cutoff = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10)
  const formula = `AND(NOT({Project Record UID (from Projects)}=BLANK()),IS_AFTER({Date},'${cutoff}'))`
  const recs = await apiGetAll(TBL_INTER, 'filterByFormula=' + encodeURIComponent(formula))
  return recs.map(normInteraction)
}

/**
 * Bare "prospecting" touches — logged with neither a Lead nor a Project link. This is new-business
 * outreach that hasn't become a tracked Lead yet (a cold call, a networking conversation, a social
 * post) — the firm-wide outbound-activity signal, distinct from working leads/opportunities already
 * in the system.
 */
export async function getProspectingActivity({ sinceISO } = {}) {
  const clauses = ['{Link to Lead}=BLANK()', '{Project Record UID (from Projects)}=BLANK()']
  if (sinceISO) clauses.push(`IS_AFTER({Date},'${String(sinceISO).slice(0, 10)}')`)
  const formula = `AND(${clauses.join(',')})`
  const recs = await apiGetAll(TBL_INTER, 'filterByFormula=' + encodeURIComponent(formula))
  return recs.map(normInteraction)
}

/* ── Write ───────────────────────────────────────────────────────────────── */

/**
 * Log an interaction. Provide one of { leadId } or { projectId } (or both); optional contactIds.
 * @param {{ type?, leadActivityType?, opportunityType?, notes?, date?, plannedNext?, nextDate?,
 *           teamMemberId?, leadId?, projectId?, contactIds?, voicemail? }} input
 */
export async function logInteraction(input = {}) {
  const f = {}
  if (input.type) f['Type'] = input.type
  if (input.leadActivityType) f['Lead Activity Type'] = input.leadActivityType
  if (input.opportunityType) f['Opportunity Type'] = input.opportunityType
  if (input.notes) f['Notes'] = input.notes
  f['Date'] = input.date || new Date().toISOString()
  if (input.plannedNext) f['Planned Next Interaction'] = input.plannedNext
  if (input.nextDate) f['Next Interaction Date'] = input.nextDate
  if (input.voicemail) f['Voicemail'] = true
  if (input.teamMemberId) f['Team Member'] = [input.teamMemberId]
  if (input.leadId) f['Link to Lead'] = [input.leadId]
  if (input.projectId) f['Projects'] = [input.projectId]
  if (Array.isArray(input.contactIds) && input.contactIds.length) f['Contact'] = input.contactIds
  const res = throwOnError(await apiPost(TBL_INTER, f, { typecast: true }), 'interaction')
  return res && res.id ? normInteraction(res) : null
}

/** Edit an interaction's notes and/or date (inline timeline edit). */
export async function updateInteraction(id, { notes, date } = {}) {
  const f = {}
  if (notes !== undefined) f['Notes'] = notes
  if (date !== undefined) f['Date'] = date
  const res = throwOnError(await apiPatch(TBL_INTER, id, f, { typecast: true }), 'interaction')
  return res && res.id ? normInteraction(res) : null
}

/** Delete an interaction. */
export function deleteInteraction(id) {
  return apiDelete(TBL_INTER, id)
}
